import { ErrorOption, FieldValues } from "react-hook-form";
import { NetworkError } from "../common/models";
import { TOptionsBase } from "i18next";
import { HTTPError } from "ky";
import { getI18n } from "react-i18next";
import { showToast } from "./toastHelpers";
import * as Sentry from "@sentry/react";

export const getApiErrorCode = (error: NetworkError) => {
  let errorCode = "unknown";

  if (error?.response?.data?.errorCode) {
    return `${error.response.data.errorCode}`;
  }

  if (error && error.message === "Network Error") {
    return "network";
  }

  if (!error?.response) {
    return "unknown";
  }

  if (error.response.status === 401) {
    return ""; // no error message
  }

  errorCode = "serverUnknown";

  if (error.response.status === 403) {
    errorCode = "noPermission";
  }

  return errorCode;
};

export const handleApiError = (error: NetworkError) => {
  if (error.response) {
    // Server errors
    if (error.response.status !== 403 && error.response.status !== 401) {
      Sentry.captureException(error);
    }
  } else if (error.message !== "Network Error") {
    // Local errors
    Sentry.captureException(error);
  }
};

export const handleApiErrorWithNotification = (error: NetworkError) => {
  const { t } = getI18n();
  handleApiError(error);
  const errorCode = getApiErrorCode(error);
  if (errorCode) {
    showToast(t(errorCode, { ns: "errors" }), "error");
  }
};

export const handleFormServerErrors = <T extends FieldValues>(
  e: unknown,
  t: (
    key: string,
    options?: TOptionsBase & Record<string, number | string | undefined>,
  ) => string,
  setError: (
    name: keyof T,
    error: ErrorOption,
    options?: {
      shouldFocus: boolean;
    },
  ) => void,
) => {
  const error = e as HTTPError;

  // wrong login credentials
  if (
    error.response &&
    error.response.status === 403 &&
    error.name === "_error" &&
    error.message
  ) {
    return setError("root", {
      message: t(error.message),
    });
  }

  if (
    error.response &&
    error.response.status === 400 &&
    error.name &&
    error.message
  ) {
    if (error.name === "_error") {
      return setError("root", {
        message: t(error.message),
      });
    } else {
      return setError(error.name, {
        message: t(error.message),
      });
    }
  } else {
    handleApiError(error);
    return setError("root", { message: t(getApiErrorCode(error)) });
  }
};
